import React, { useRef, useState } from 'react'
import o from '../../components/Overlay.module.css'
import styles from '../TeamsPage.module.css'
import { useLayer } from '../../components/layers'
import { inductionEmail } from './inductionEmail'

// An induction team's email template: the instructions an office sends new
// starters, with this team's code in them, ready to copy into Outlook. The
// subject and the body copy separately, since a mail program takes them in
// different boxes.

interface Props {
  teamName: string
  clientName: string
  code: string
  // When the team's code is replaced, after which a sent email stops working.
  expiresOn: string | null
  onClose: () => void
}

// The body as HTML, so Outlook keeps its bold and its link, with plain text
// beside it; text alone where the browser cannot write HTML.
async function copyRich(html: string, text: string) {
  if (typeof ClipboardItem !== 'undefined' && navigator.clipboard?.write) {
    await navigator.clipboard.write([
      new ClipboardItem({
        'text/html': new Blob([html], { type: 'text/html' }),
        'text/plain': new Blob([text], { type: 'text/plain' }),
      }),
    ])
  } else await navigator.clipboard.writeText(text)
}

export function InductionEmailDialog({ teamName, clientName, code, expiresOn, onClose }: Props) {
  const panelEl = useRef<HTMLDivElement>(null)
  const { requestClose } = useLayer(true, onClose, { el: panelEl, modal: true })
  const [copied, setCopied] = useState<'subject' | 'body' | null>(null)
  const [failed, setFailed] = useState(false)
  const mail = inductionEmail({ clientName, code, downloadUrl: `${window.location.origin}/crewapp` })

  const copy = async (what: 'subject' | 'body') => {
    setFailed(false)
    try {
      if (what === 'subject') await navigator.clipboard.writeText(mail.subject)
      else await copyRich(mail.html, mail.text)
      setCopied(what)
    } catch {
      setFailed(true)
    }
  }

  return (
    <div className={o.scrim} onClick={(e) => e.target === e.currentTarget && requestClose()}>
      <div ref={panelEl} tabIndex={-1} className={`${o.modal} ${styles.qrModal} ${styles.mailModal}`} role="dialog" aria-label={`Induction email: ${teamName}`}>
        <div className={styles.qrHead}>
          <div className={styles.qrHeadText}>
            <div className={styles.qrKind}>Induction email</div>
            <h2 className={styles.qrTeam}>{teamName}</h2>
          </div>
          <button type="button" className={styles.qrClose} aria-label="Close" onClick={requestClose}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.75" strokeLinecap="round" aria-hidden="true">
              <path d="M18 6 6 18M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className={styles.mailStage}>
          <p className={styles.mailLead}>
            Copy the subject and the email into Outlook and send it to your new starters. It carries this team’s code, <b>{code}</b>
            {expiresOn ? `, which works until ${expiresOn}. After that the team gets a new code, so send this again.` : '.'}
          </p>
          <div className={styles.mailField}>
            <span className={styles.mailLabel}>Subject</span>
            <span className={styles.mailSubject} data-testid="mail-subject">
              {mail.subject}
            </span>
            <button type="button" className={o.ghost} onClick={() => copy('subject')}>
              {copied === 'subject' ? 'Copied' : 'Copy'}
            </button>
          </div>
          <div className={styles.mailBody} data-testid="mail-body" dangerouslySetInnerHTML={{ __html: mail.html }} />
          {failed && (
            <div className={styles.schedError} role="alert">
              The browser would not copy. Select the email above and copy it with Ctrl+C.
            </div>
          )}
        </div>

        <div className={styles.qrFoot}>
          <button type="button" className={o.ghost} onClick={requestClose}>
            Close
          </button>
          <button type="button" className={`${styles.qrPrint} ${styles.qrPdf}`} onClick={() => copy('body')}>
            {copied === 'body' ? 'Email copied' : 'Copy email'}
          </button>
        </div>
      </div>
    </div>
  )
}
